import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './provision-of-unpaid-care.reducer';

export const ProvisionOfUnpaidCare = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const provisionOfUnpaidCareList = useAppSelector(state => state.provisionOfUnpaidCare.entities);
  const loading = useAppSelector(state => state.provisionOfUnpaidCare.loading);
  const totalItems = useAppSelector(state => state.provisionOfUnpaidCare.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="provision-of-unpaid-care-heading" data-cy="ProvisionOfUnpaidCareHeading">
        <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.home.title">Provision Of Unpaid Cares</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/provision-of-unpaid-care/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.home.createLabel">Create new Provision Of Unpaid Care</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {provisionOfUnpaidCareList && provisionOfUnpaidCareList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.totalAllUsualResidents">Total All Usual Residents</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('providesNoCare')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.providesNoCare">Provides No Care</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('providesNoCare')} />
                </th>
                <th className="hand" onClick={sort('provides1To19Hours')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.provides1To19Hours">Provides 1 To 19 Hours</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('provides1To19Hours')} />
                </th>
                <th className="hand" onClick={sort('provides20To49Hours')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.provides20To49Hours">Provides 20 To 49 Hours</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('provides20To49Hours')} />
                </th>
                <th className="hand" onClick={sort('provides50OrMoreHours')}>
                  <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.provides50OrMoreHours">Provides 50 Or More Hours</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('provides50OrMoreHours')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {provisionOfUnpaidCareList.map((provisionOfUnpaidCare, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/provision-of-unpaid-care/${provisionOfUnpaidCare.id}`} color="link" size="sm">
                      {provisionOfUnpaidCare.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${provisionOfUnpaidCare.geographicLevel}`} />
                  </td>
                  <td>{provisionOfUnpaidCare.date}</td>
                  <td>{provisionOfUnpaidCare.geography}</td>
                  <td>{provisionOfUnpaidCare.geographyCode}</td>
                  <td>{provisionOfUnpaidCare.totalAllUsualResidents}</td>
                  <td>{provisionOfUnpaidCare.providesNoCare}</td>
                  <td>{provisionOfUnpaidCare.provides1To19Hours}</td>
                  <td>{provisionOfUnpaidCare.provides20To49Hours}</td>
                  <td>{provisionOfUnpaidCare.provides50OrMoreHours}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/provision-of-unpaid-care/${provisionOfUnpaidCare.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/provision-of-unpaid-care/${provisionOfUnpaidCare.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/provision-of-unpaid-care/${provisionOfUnpaidCare.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.provisionOfUnpaidCare.home.notFound">No Provision Of Unpaid Cares found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={provisionOfUnpaidCareList && provisionOfUnpaidCareList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ProvisionOfUnpaidCare;
